import { abs, float, max, mix, normalize, select, sign, step, vec2, vec3 } from 'three/tsl';
import type { Vector3 } from 'three/webgpu';

type N = any;

/*
 * Octahedral maps: a sphere of directions folded onto a square, for maps of the whole wall
 * seen from a hollow's centre (the world panorama, the lava's flares).
 */

/** Unit direction → octahedral map coordinates (0..1, as screenUV in the pass that wrote it). */
export function octEncode(d: N): N {
  const n = d.div(abs(d.x).add(abs(d.y)).add(abs(d.z)));
  // Branch-free (select() becomes if/else): mix by the hemisphere, with sign() for the fold.
  const folded = vec2(float(1).sub(abs(n.y)), float(1).sub(abs(n.x))).mul(sign(n.xy));
  return mix(n.xy, folded, step(n.z, 0)).mul(0.5).add(0.5);
}

/** Octahedral map coordinates (0..1) → unit direction. */
export function octDecode(uv: N): N {
  const f = uv.mul(2).sub(1);
  const z = float(1).sub(abs(f.x)).sub(abs(f.y));
  const t = max(z.negate(), 0);
  const x = f.x.add(select(f.x.greaterThanEqual(0), t.negate(), t));
  const y = f.y.add(select(f.y.greaterThanEqual(0), t.negate(), t));
  return normalize(vec3(x, y, z));
}

/** octDecode on the CPU. */
export function octDecodeCpu(u: number, v: number, out: Vector3): Vector3 {
  const fx = u * 2 - 1;
  const fy = v * 2 - 1;
  const z = 1 - Math.abs(fx) - Math.abs(fy);
  const t = Math.max(-z, 0);
  const x = fx + (fx >= 0 ? -t : t);
  const y = fy + (fy >= 0 ? -t : t);
  return out.set(x, y, z).normalize();
}

/** octEncode on the CPU: [u, v] in 0..1. */
export function octEncodeCpu(d: Vector3): [number, number] {
  const s = Math.abs(d.x) + Math.abs(d.y) + Math.abs(d.z);
  let x = d.x / s;
  let y = d.y / s;
  if (d.z < 0) {
    const fx = (1 - Math.abs(y)) * (x >= 0 ? 1 : -1);
    const fy = (1 - Math.abs(x)) * (y >= 0 ? 1 : -1);
    x = fx;
    y = fy;
  }
  return [x * 0.5 + 0.5, y * 0.5 + 0.5];
}
